import { useMemo, useEffect, useState } from 'react';
import { MetaPage, PageCategory } from '../types/meta.types';
import { Facebook, Search, ExternalLink, Settings, RefreshCw, Trash2, TriangleAlert, MoreVertical, Bug, Copy, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

/** Página com envio pausado pelo bloqueio automático.
 *  Compara a DATA (não um flag) com hoje: o backend grava envio_bloqueado_ate
 *  com validade curta justamente para o bloqueio expirar sozinho se o job
 *  diário parar de rodar. A tela respeita a mesma regra. */
function isPausada(page: { envio_bloqueado_ate?: string | null }): boolean {
    if (!page.envio_bloqueado_ate) return false;
    const hoje = new Date().toISOString().slice(0, 10);
    return page.envio_bloqueado_ate.slice(0, 10) >= hoje;
}
import { Checkbox } from '@/components/ui/checkbox';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRef } from 'react';
import { bulkUpdateMetaPageConfig, getPageCategories, debugPageToken, getUsersPagesMap } from '@/services/api/connections';
import { ConnectedUserPagesEntry } from '../types/meta.types';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { getCampanhasDuplicadas, type CampanhaDuplicadaGrupo } from '@/services/api/connections';
import CampanhaDuplicadaFixModal from './CampanhaDuplicadaFixModal';
import AppSemTokenFixModal, { type PaginaSemToken } from './AppSemTokenFixModal';
import { TIMEZONES, LANGUAGES, CURRENCIES } from '../constants/config-options';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ConnectedProfilesCell } from './ConnectedProfilesCell';
import { listAppPageGroups } from '@/services/api/app-page-groups';
import FiltroUsuarioAdmin, { useFiltroUsuarioAdmin } from '@/components/FiltroUsuarioAdmin';


interface PagesListProps {
    pages: MetaPage[];
    onToggleStatus: (page: MetaPage) => void;
    onConfigure: (page: MetaPage) => void;
    onSyncLeads?: (page: MetaPage) => void;
    onConnect?: () => void;
    onDeletePage?: (page: MetaPage) => void;
}

function dominioDe(url: string): string {
    const host = url.trim().replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
    return host.toLowerCase().replace(/^www\./, '');
}

function RowMenu({ onSyncLeads, onDetails, onDebugToken, onDelete }: { onSyncLeads: () => void; onDetails: () => void; onDebugToken: () => void; onDelete: () => void }) {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
                btnRef.current && !btnRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    function handleOpen(e: React.MouseEvent) {
        e.stopPropagation();
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 4, left: rect.right - 180 });
        setOpen(o => !o);
    }

    return (
        <div className="flex justify-end">
            <button
                ref={btnRef}
                onClick={handleOpen}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
                <MoreVertical className="w-4 h-4" />
            </button>
            {open && (
                <div
                    ref={menuRef}
                    style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
                    className="bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[180px]"
                >
                    <button
                        onClick={e => { e.stopPropagation(); setOpen(false); onSyncLeads(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <RefreshCw className="w-4 h-4 text-gray-400" />
                        Sync Leads
                    </button>
                    <button
                        onClick={e => { e.stopPropagation(); setOpen(false); onDetails(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Settings className="w-4 h-4 text-gray-400" />
                        Detalhes
                    </button>
                    <button
                        onClick={e => { e.stopPropagation(); setOpen(false); onDebugToken(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Bug className="w-4 h-4 text-gray-400" />
                        Debug Token
                    </button>
                    <button
                        onClick={e => { e.stopPropagation(); setOpen(false); onDelete(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                        <Trash2 className="w-4 h-4 text-red-400" />
                        Excluir
                    </button>
                </div>
            )}
        </div>
    );
}

export default function PagesList({ pages, onToggleStatus: _onToggleStatus, onConfigure: _onConfigure, onSyncLeads, onConnect, onDeletePage }: PagesListProps) {
    const [searchTerm, setSearchTerm] = useState('');
    // Admin recebe as páginas de todos; o filtro "Usuário" recorta por dono.
    const { isAdmin, usuarioId: donoFiltro } = useFiltroUsuarioAdmin();
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedLanguage, setSelectedLanguage] = useState('all');
    const [selectedTimezone, setSelectedTimezone] = useState('all');
    const [selectedDomain, setSelectedDomain] = useState('all');
    const [searchParams, setSearchParams] = useSearchParams();
    // Fonte única de verdade: deriva direto da URL (?filter=problem), sem estado duplicado.
    // Isso mantém toggle/lista sempre coerentes com a URL, inclusive em navegação "voltar".
    const onlyWithWarn = searchParams.get('filter') === 'problem';
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 100;
    const navigate = useNavigate();
    const { toast } = useToast();

    function handleToggleOnlyWithWarn() {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (onlyWithWarn) {
                params.delete('filter');
            } else {
                params.set('filter', 'problem');
            }
            return params;
        }, { replace: true });
    }

    // Bulk selection state
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    // ── Campanhas duplicadas ────────────────────────────────────────────────
    // Página que divide `UTM_CAMPAIGN` (ou `UTM_CAMPAIGN_BROAD` entre idiomas
    // diferentes) com outra tem a receita DIVIDIDA na Tela de Leads: a origem
    // devolve um valor por utm_campaign e não diz quem gerou o quê. Marcar
    // aqui é o único lugar onde quem cuida do cadastro vai ver.
    const [grupoEmAjuste, setGrupoEmAjuste] = useState<CampanhaDuplicadaGrupo | null>(null);
    const onlyDuplicated = searchParams.get('filter') === 'campanha';

    const { data: duplicadas, refetch: refetchDuplicadas } = useQuery({
        queryKey: ['campanhas-duplicadas-paginas'],
        queryFn: () => getCampanhasDuplicadas(),
        staleTime: 60 * 1000,
    });

    // page_id da Meta -> grupos que a tocam. A lista tem o page_id da Meta;
    // o endpoint devolve os dois ids, então o cruzamento é direto.
    const duplicadasPorPagina = useMemo(() => {
        const mapa = new Map<string, CampanhaDuplicadaGrupo[]>();
        for (const g of duplicadas?.grupos ?? []) {
            for (const p of g.paginas) {
                const atual = mapa.get(p.page_id) ?? [];
                atual.push(g);
                mapa.set(p.page_id, atual);
            }
        }
        return mapa;
    }, [duplicadas]);

    function handleToggleOnlyDuplicated() {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (onlyDuplicated) params.delete('filter');
            else params.set('filter', 'campanha');
            return params;
        }, { replace: true });
    }
    const [bulkLanguage, setBulkLanguage] = useState('');
    const [bulkCategoryId, setBulkCategoryId] = useState('');
    const [bulkTimezone, setBulkTimezone] = useState('');
    const [bulkCurrency, setBulkCurrency] = useState('');
    const [categories, setCategories] = useState<PageCategory[]>([]);
    const [isBulkLoading, setIsBulkLoading] = useState(false);
    const [connectedUsers, setConnectedUsers] = useState<ConnectedUserPagesEntry[]>([]);
    const [selectedConnectedUserId, setSelectedConnectedUserId] = useState('all');
    // Sem o mapa de perfis carregado toda página com grupo pareceria sem token.
    const [perfisCarregados, setPerfisCarregados] = useState(false);

    // Load categories for bulk dropdown (fire once)
    useEffect(() => {
        getPageCategories().then(setCategories).catch(() => {});
    }, []);

    // Load meta_users conectados + páginas vinculadas (meta_page_tokens) — fire once
    useEffect(() => {
        getUsersPagesMap().then(u => { setConnectedUsers(u); setPerfisCarregados(true); }).catch(() => {});
    }, []);

    const connectedUserOptions = useMemo(
        () => connectedUsers.map(u => ({
            value: String(u.meta_user_id),
            label: u.meta_user_name ? `${u.meta_user_name} (${u.app_name ?? 'sem app'})` : `Perfil #${u.meta_user_id}`,
        })),
        [connectedUsers]
    );

    const connectedUserPageIds = useMemo(() => {
        if (selectedConnectedUserId === 'all') return null;
        const entry = connectedUsers.find(u => String(u.meta_user_id) === selectedConnectedUserId);
        return entry ? new Set(entry.pages.map(p => p.page_id)) : new Set<string>();
    }, [selectedConnectedUserId, connectedUsers]);

    // Inverte connectedUsers (perfil -> páginas) pra (page_id -> perfis),
    // pra alimentar a coluna "Perfis conectados" sem 1 request por linha.
    const profilesByPageId = useMemo(() => {
        const index = new Map<string, { meta_user_id: number; meta_user_name: string | null; app_id?: number; app_name: string | null; token_status: string | null }[]>();
        connectedUsers.forEach(u => {
            u.pages.forEach(p => {
                const list = index.get(p.page_id) ?? [];
                list.push({
                    meta_user_id: u.meta_user_id,
                    meta_user_name: u.meta_user_name,
                    app_id: p.app_id,
                    app_name: u.app_name,
                    token_status: p.token_status,
                });
                index.set(p.page_id, list);
            });
        });
        return index;
    }, [connectedUsers]);

    // Mesma chave da aba Grupos de App: salvar lá invalida e atualiza aqui.
    const { data: appGroups = [] } = useQuery({
        queryKey: ['app-page-groups'],
        queryFn: () => listAppPageGroups(),
        staleTime: 60 * 1000,
    });

    // page_id -> app travado pelo grupo. Página em dois grupos de apps
    // diferentes é conflito: o orquestrador ignora a trava e cai no
    // least-loaded, então a tela não pode apontar um dos dois como priorizado.
    const appGroupByPageId = useMemo(() => {
        const map = new Map<string, { appId: number; appName: string | null; donoId: number | null; conflito: boolean }>();
        for (const g of appGroups) {
            for (const p of g.pages) {
                const atual = map.get(p.page_id);
                if (atual && atual.appId !== g.app_id) atual.conflito = true;
                else map.set(p.page_id, { appId: g.app_id, appName: g.app_name, donoId: g.user_id ?? null, conflito: false });
            }
        }
        return map;
    }, [appGroups]);

    // Perfil com token vivo no app do grupo: é o token que o envio usa.
    // Sem ele a trava não pega e a página cai pra outro app.
    const perfisNoAppDoGrupo = (pageId: string, grupo: { appId: number; appName: string | null }) =>
        (profilesByPageId.get(pageId) ?? []).filter(p =>
            (p.app_id != null ? p.app_id === grupo.appId : p.app_name === grupo.appName)
            && (!p.token_status || p.token_status === 'ok'));

    // ── Priorizada em app sem token ─────────────────────────────────────────
    // Não some sozinho: enquanto a página estiver travada num app em que não
    // tem token, o aviso e o selo continuam na tela.
    const [semTokenEmAjuste, setSemTokenEmAjuste] = useState<PaginaSemToken | null>(null);
    const onlySemToken = searchParams.get('filter') === 'sem-token';

    const semTokenPorPagina = useMemo(() => {
        const set = new Set<string>();
        if (!perfisCarregados) return set;
        for (const [pageId, grupo] of appGroupByPageId) {
            if (!grupo.conflito && perfisNoAppDoGrupo(pageId, grupo).length === 0) set.add(pageId);
        }
        return set;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appGroupByPageId, profilesByPageId, perfisCarregados]);

    const totalSemToken = useMemo(
        () => pages.filter(p => semTokenPorPagina.has(p.page_id)).length,
        [pages, semTokenPorPagina]
    );

    function abrirAjusteSemToken(page: MetaPage) {
        const grupo = appGroupByPageId.get(page.page_id);
        if (!grupo) return;
        setSemTokenEmAjuste({
            pageId: page.page_id,
            pageName: page.page_name,
            donoId: grupo.donoId ?? page.user_id,
            appId: grupo.appId,
            appName: grupo.appName,
        });
    }

    function handleToggleOnlySemToken() {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (onlySemToken) params.delete('filter');
            else params.set('filter', 'sem-token');
            return params;
        }, { replace: true });
    }

    const categoryOptions = useMemo(() => {
        const unique = new Set<string>();
        pages.forEach(p => { if (p.internal_category_name) unique.add(p.internal_category_name); });
        return Array.from(unique).sort();
    }, [pages]);

    const languageOptions = useMemo(() => {
        const unique = new Set<string>();
        pages.forEach(p => { if (p.language) unique.add(p.language); });
        return Array.from(unique).sort().map(l => ({ value: l, label: l }));
    }, [pages]);

    const timezoneOptions = useMemo(() => {
        const unique = new Set<string>();
        pages.forEach(p => { if (p.timezone) unique.add(p.timezone); });
        return Array.from(unique).sort().map(t => ({ value: t, label: t }));
    }, [pages]);

    const domainOptions = useMemo(() => {
        const unique = new Set<string>();
        pages.forEach(p => { if (p.domain) unique.add(dominioDe(p.domain)); });
        return Array.from(unique).sort().map(d => ({ value: d, label: d }));
    }, [pages]);

    // Filtro client-side
    const filteredPages = pages.filter(page => {
        const matchesSearch = !searchTerm ||
            page.page_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            String(page.page_id).includes(searchTerm.trim());
        const matchesCategory = selectedCategory === 'all' || page.internal_category_name === selectedCategory;
        const matchesLanguage = selectedLanguage === 'all' || page.language === selectedLanguage;
        const matchesTimezone = selectedTimezone === 'all' || page.timezone === selectedTimezone;
        const matchesDomain = selectedDomain === 'all' || (!!page.domain && dominioDe(page.domain) === selectedDomain);
        const matchesWarn = !onlyWithWarn || (page.token_status && page.token_status !== 'ok');
        const matchesDup = !onlyDuplicated || duplicadasPorPagina.has(String(page.page_id));
        const matchesSemToken = !onlySemToken || semTokenPorPagina.has(page.page_id);
        const matchesConnectedUser = !connectedUserPageIds || connectedUserPageIds.has(page.page_id);
        const matchesOwner = donoFiltro == null || page.user_id === donoFiltro;
        return matchesSearch && matchesCategory && matchesLanguage && matchesTimezone && matchesDomain && matchesWarn && matchesDup && matchesSemToken && matchesConnectedUser && matchesOwner;
    });

    // Paginação client-side
    const totalPages = Math.ceil(filteredPages.length / perPage);
    const paginatedPages = filteredPages.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Reset página e seleção quando filtro muda
    useEffect(() => {
        setCurrentPage(1);
        setSelectedIds(new Set());
    }, [searchTerm, selectedCategory, selectedLanguage, selectedTimezone, selectedDomain, onlyWithWarn, onlyDuplicated, onlySemToken, connectedUserPageIds, donoFiltro]);

    // Checkbox handlers
    const allCurrentSelected = paginatedPages.length > 0 && paginatedPages.every(p => selectedIds.has(p.id));
    const someCurrentSelected = paginatedPages.some(p => selectedIds.has(p.id)) && !allCurrentSelected;

    function handleToggleAll() {
        if (allCurrentSelected) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                paginatedPages.forEach(p => next.delete(p.id));
                return next;
            });
        } else {
            setSelectedIds(prev => {
                const next = new Set(prev);
                paginatedPages.forEach(p => next.add(p.id));
                return next;
            });
        }
    }

    function handleToggleRow(id: number) {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }

    async function handleBulkApply() {
        if (selectedIds.size === 0) return;
        const payload: { page_ids: number[]; language?: string; internal_category_id?: number | null; timezone?: string; currency?: string } = {
            page_ids: Array.from(selectedIds),
        };
        if (bulkLanguage) payload.language = bulkLanguage;
        if (bulkTimezone) payload.timezone = bulkTimezone;
        if (bulkCurrency) payload.currency = bulkCurrency;
        if (bulkCategoryId !== '') payload.internal_category_id = bulkCategoryId === 'null' ? null : Number(bulkCategoryId);

        setIsBulkLoading(true);
        try {
            const result = await bulkUpdateMetaPageConfig(payload);
            toast({
                title: `${result.updated} página(s) atualizadas`,
                description: result.failed.length > 0 ? `${result.failed.length} falha(s).` : undefined,
            });
            setSelectedIds(new Set());
            setBulkLanguage('');
            setBulkCategoryId('');
            setBulkTimezone('');
            setBulkCurrency('');
        } catch {
            toast({ title: 'Erro ao atualizar páginas', variant: 'destructive' });
        } finally {
            setIsBulkLoading(false);
        }
    }

    async function handleDebugToken(page: MetaPage) {
        try {
            const result = await debugPageToken(page.page_id);
            if (result.valid) {
                toast({ title: 'Token válido ✅' });
            } else {
                toast({ title: `Token inválido: ${result.error ?? 'motivo desconhecido'}`, variant: 'destructive' });
            }
        } catch {
            toast({ title: 'Erro ao testar token da página', variant: 'destructive' });
        }
    }

    function handleBulkCancel() {
        setSelectedIds(new Set());
        setBulkLanguage('');
        setBulkCategoryId('');
        setBulkTimezone('');
        setBulkCurrency('');
    }

    if (pages.length === 0) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Páginas</h1>
                        <p className="mt-2 text-gray-600">Gerencie as páginas vinculadas ao Meta Ads.</p>
                    </div>
                    <Button
                        onClick={onConnect}
                        className="bg-[#475162] hover:bg-[#3a4151] text-white font-medium px-6 py-2.5 rounded-xl border border-[#475162] transition-all duration-300 hover:shadow-lg"
                    >
                        <Facebook className="mr-2 h-4 w-4 fill-white" />
                        Vincular Página
                    </Button>
                </div>

                <div className="bg-white rounded-lg p-16 text-center shadow border border-gray-100">
                    <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                        <Facebook className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Nenhuma página vinculada
                    </h3>
                    <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                        Conecte sua conta do Facebook para começar a gerenciar suas páginas.
                    </p>
                    <Button onClick={onConnect} variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50">
                        Conectar Agora
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Páginas</h1>
                    <p className="mt-2 text-gray-600">Gerencie suas páginas vinculadas e configurações.</p>
                </div>
                <Button
                    onClick={onConnect}
                    className="bg-[#475162] hover:bg-[#3a4151] text-white font-medium px-6 py-2.5 rounded-xl border border-[#475162] transition-all duration-300 hover:shadow-lg"
                >
                    <Facebook className="mr-2 h-4 w-4 fill-white" />
                    Vincular Página
                </Button>
            </div>

            {/* Filter Section */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Buscar por nome
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Página..."
                                className="pl-9"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Categoria
                        </label>
                        <SearchableSelect
                            value={selectedCategory}
                            onValueChange={setSelectedCategory}
                            options={[{ value: 'all', label: 'Todas categorias' }, ...categoryOptions.map(cat => ({ value: cat, label: cat }))]}
                            placeholder="Todas categorias"
                        />
                    </div>

                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Idioma
                        </label>
                        <SearchableSelect
                            value={selectedLanguage}
                            onValueChange={setSelectedLanguage}
                            options={[{ value: 'all', label: 'Todos os idiomas' }, ...languageOptions]}
                            placeholder="Todos os idiomas"
                        />
                    </div>

                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Fuso Horário
                        </label>
                        <SearchableSelect
                            value={selectedTimezone}
                            onValueChange={setSelectedTimezone}
                            options={[{ value: 'all', label: 'Todos os fusos' }, ...timezoneOptions]}
                            placeholder="Todos os fusos"
                        />
                    </div>

                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Domínio
                        </label>
                        <SearchableSelect
                            value={selectedDomain}
                            onValueChange={setSelectedDomain}
                            options={[{ value: 'all', label: 'Todos os domínios' }, ...domainOptions]}
                            placeholder="Todos os domínios"
                        />
                    </div>

                    {isAdmin && (
                        <div className="flex-1 min-w-48 space-y-1.5">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                                Usuário
                            </label>
                            <FiltroUsuarioAdmin className="w-full" />
                        </div>
                    )}

                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Conectado por
                        </label>
                        <SearchableSelect
                            value={selectedConnectedUserId}
                            onValueChange={setSelectedConnectedUserId}
                            options={[{ value: 'all', label: 'Todos os perfis' }, ...connectedUserOptions]}
                            placeholder="Todos os perfis"
                        />
                    </div>

                    <button
                        type="button"
                        role="switch"
                        aria-checked={onlyWithWarn}
                        onClick={handleToggleOnlyWithWarn}
                        className="flex items-center gap-2 cursor-pointer pb-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                        <div
                            className={`relative w-9 h-5 rounded-full transition-colors ${onlyWithWarn ? 'bg-red-500' : 'bg-gray-200'}`}
                        >
                            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${onlyWithWarn ? 'translate-x-4' : 'translate-x-0'}`} />
                        </div>
                        <div className="flex items-center gap-1.5">
                            <TriangleAlert className={`h-3.5 w-3.5 ${onlyWithWarn ? 'text-red-500' : 'text-gray-400'}`} />
                            <span className="text-sm text-gray-600 whitespace-nowrap">Com problema</span>
                        </div>
                    </button>

                    <button
                        type="button"
                        role="switch"
                        aria-checked={onlyDuplicated}
                        onClick={handleToggleOnlyDuplicated}
                        className="flex items-center gap-2 cursor-pointer pb-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                        <div className={`relative w-9 h-5 rounded-full transition-colors ${onlyDuplicated ? 'bg-amber-500' : 'bg-gray-200'}`}>
                            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${onlyDuplicated ? 'translate-x-4' : 'translate-x-0'}`} />
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Copy className={`h-3.5 w-3.5 ${onlyDuplicated ? 'text-amber-500' : 'text-gray-400'}`} />
                            <span className="text-sm text-gray-600 whitespace-nowrap">Campanha duplicada</span>
                        </div>
                    </button>

                    <button
                        type="button"
                        role="switch"
                        aria-checked={onlySemToken}
                        onClick={handleToggleOnlySemToken}
                        className="flex items-center gap-2 cursor-pointer pb-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    >
                        <div className={`relative w-9 h-5 rounded-full transition-colors ${onlySemToken ? 'bg-red-500' : 'bg-gray-200'}`}>
                            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${onlySemToken ? 'translate-x-4' : 'translate-x-0'}`} />
                        </div>
                        <div className="flex items-center gap-1.5">
                            <KeyRound className={`h-3.5 w-3.5 ${onlySemToken ? 'text-red-500' : 'text-gray-400'}`} />
                            <span className="text-sm text-gray-600 whitespace-nowrap">Sem token no app</span>
                        </div>
                    </button>
                </div>
            </div>

            {/* Receita dividida sem ninguém pedir: a tela de cadastro é onde
                isso se resolve, então o aviso mora aqui. */}
            {duplicadas && duplicadas.total_grupos > 0 && !onlyDuplicated && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
                    <Copy className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-amber-900">
                        <strong>{duplicadas.total_grupos} campanha(s) aparecem em mais de uma página</strong>,
                        afetando {duplicadas.paginas_afetadas} páginas. Enquanto for assim, a receita é
                        dividida em partes iguais entre elas e nenhuma tem número próprio na Tela de Leads.
                        {duplicadas.grupos_flow > 0 && (
                            <> {duplicadas.grupos_flow} são de <strong>flow</strong>, que deveria ter uma
                            campanha por página — essas são erro de cadastro.</>
                        )}
                        {' '}
                        <button
                            type="button"
                            onClick={handleToggleOnlyDuplicated}
                            className="underline font-medium hover:text-amber-700"
                        >
                            ver só essas páginas
                        </button>
                    </div>
                </div>
            )}

            {totalSemToken > 0 && !onlySemToken && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3">
                    <KeyRound className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-red-900">
                        <strong>{totalSemToken} página(s) priorizadas num app em que não têm token.</strong>{' '}
                        A prioridade não vale e o envio usa outro app. Mova a prioridade para um app com
                        token ou tire a prioridade.{' '}
                        <button
                            type="button"
                            onClick={handleToggleOnlySemToken}
                            className="underline font-medium hover:text-red-700"
                        >
                            ver só essas páginas
                        </button>
                    </div>
                </div>
            )}

            {/* Table Header Stats */}
            <div className="px-1">
                <span className="text-sm text-gray-500">
                    Mostrando <strong className="text-gray-900">{paginatedPages.length}</strong> de <strong className="text-gray-900">{filteredPages.length}</strong> páginas
                </span>
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-3">
                    <span className="text-sm font-medium text-blue-800 whitespace-nowrap">
                        {selectedIds.size} selecionada(s)
                    </span>
                    <SearchableSelect
                        value={bulkCategoryId}
                        onValueChange={setBulkCategoryId}
                        options={[{ value: 'null', label: 'Sem categoria' }, ...categories.map(c => ({ value: String(c.id), label: c.name }))]}
                        placeholder="Categoria..."
                        className="h-8 w-44 text-sm"
                    />
                    <SearchableSelect
                        value={bulkLanguage}
                        onValueChange={setBulkLanguage}
                        options={LANGUAGES}
                        placeholder="Idioma..."
                        className="h-8 w-52 text-sm"
                    />
                    <SearchableSelect
                        value={bulkTimezone}
                        onValueChange={setBulkTimezone}
                        options={TIMEZONES}
                        placeholder="Fuso Horário..."
                        className="h-8 w-52 text-sm"
                    />
                    <SearchableSelect
                        value={bulkCurrency}
                        onValueChange={setBulkCurrency}
                        options={CURRENCIES}
                        placeholder="Moeda..."
                        className="h-8 w-40 text-sm"
                    />
                    <Button
                        size="sm"
                        className="h-8 bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={handleBulkApply}
                        disabled={isBulkLoading || (!bulkLanguage && !bulkTimezone && !bulkCurrency && bulkCategoryId === '')}
                    >
                        {isBulkLoading ? 'Aplicando...' : 'Aplicar'}
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-gray-600"
                        onClick={handleBulkCancel}
                        disabled={isBulkLoading}
                    >
                        Cancelar
                    </Button>
                </div>
            )}

            {/* Table Section */}
            <div className="bg-white rounded-lg shadow border border-gray-100 overflow-x-auto w-full">
                <table className="w-full divide-y divide-gray-200 table-fixed" style={{ minWidth: '1150px' }}>
                    <colgroup>
                        <col style={{ width: '17%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '7%' }} />
                        <col style={{ width: '9%' }} />
                        <col style={{ width: '11%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '11%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '44px' }} />
                    </colgroup>
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="pl-4 pr-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <div className="flex items-center gap-3">
                                    <Checkbox
                                        checked={allCurrentSelected}
                                        data-state={someCurrentSelected ? 'indeterminate' : undefined}
                                        onCheckedChange={handleToggleAll}
                                        aria-label="Selecionar todas"
                                    />
                                    Página
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Categoria
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Idioma
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Fuso Horário
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Domínio
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Perfis conectados
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Perfil priorizado
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                App priorizado
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Ações
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedPages.map((page) => (
                            <tr key={page.id} className={`hover:bg-gray-50 transition-colors ${selectedIds.has(page.id) ? 'bg-blue-50/40' : ''}`}>
                                <td className="pl-4 pr-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            checked={selectedIds.has(page.id)}
                                            onCheckedChange={() => handleToggleRow(page.id)}
                                            aria-label={`Selecionar ${page.page_name}`}
                                        />
                                        <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                                            <Facebook className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <p className="text-sm font-semibold text-gray-900 leading-none truncate">
                                                    {page.page_name}
                                                </p>
                                                <a
                                                    href={`https://facebook.com/${page.page_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-gray-400 hover:text-blue-600 shrink-0"
                                                >
                                                    <ExternalLink className="h-3 w-3" />
                                                </a>
                                                {page.token_status && page.token_status !== 'ok' && (
                                                    <TriangleAlert className="h-3.5 w-3.5 text-red-500 shrink-0" />
                                                )}
                                                {(() => {
                                                    const grupos = duplicadasPorPagina.get(String(page.page_id));
                                                    if (!grupos?.length) return null;
                                                    const erro = grupos.some(g => g.severidade === 'erro');
                                                    return (
                                                        <button
                                                            type="button"
                                                            onClick={() => setGrupoEmAjuste(grupos[0])}
                                                            title={grupos
                                                                .map(g => `${g.chave}: ${g.campanha} (${g.qtd_paginas} páginas)`)
                                                                .join(String.fromCharCode(10))}
                                                            className={`shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${erro
                                                                ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                                                                : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'}`}
                                                        >
                                                            campanha ÷{grupos[0].qtd_paginas}
                                                        </button>
                                                    );
                                                })()}
                                                {semTokenPorPagina.has(page.page_id) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => abrirAjusteSemToken(page)}
                                                        title="Priorizada num app sem token — clique para ajustar"
                                                        className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                                                    >
                                                        sem token
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5">ID: {page.page_id}</p>
                                            {page.meta_user_name && (
                                                <p className="text-xs text-gray-400">{page.meta_user_name}</p>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-4 max-w-0">
                                    {page.internal_category_name ? (
                                        <Badge variant="outline" className="font-medium text-blue-600 border-blue-100 bg-blue-50/30 max-w-full truncate block" title={page.internal_category_name}>
                                            {page.internal_category_name}
                                        </Badge>
                                    ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-700">
                                        {page.language ?? '-'}
                                    </span>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-700">
                                        {page.timezone ?? '-'}
                                    </span>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap overflow-hidden text-ellipsis">
                                    {page.domain ? (
                                        <a
                                            href={page.domain}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-blue-600 hover:underline truncate block"
                                            title={page.domain}
                                        >
                                            {page.domain.replace(/^https?:\/\//, '')}
                                        </a>
                                    ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                    <ConnectedProfilesCell profiles={profilesByPageId.get(page.page_id) ?? []} />
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap overflow-hidden">
                                    {(() => {
                                        const grupo = appGroupByPageId.get(page.page_id);
                                        if (!grupo || grupo.conflito) return <span className="text-sm text-gray-400">-</span>;
                                        const perfis = perfisNoAppDoGrupo(page.page_id, grupo);
                                        if (perfis.length === 0) {
                                            return (
                                                <button
                                                    type="button"
                                                    onClick={() => abrirAjusteSemToken(page)}
                                                    className="text-xs font-medium text-red-600 underline decoration-dotted hover:text-red-700"
                                                    title="Nenhum perfil com token ativo no app do grupo — o envio cai para outro app. Clique para ajustar."
                                                >
                                                    Sem token no app
                                                </button>
                                            );
                                        }
                                        const nomes = perfis.map(p => p.meta_user_name ?? `Perfil #${p.meta_user_id}`);
                                        return (
                                            <span className="text-sm text-gray-700 truncate block" title={nomes.join(String.fromCharCode(10))}>
                                                {nomes[0]}
                                                {nomes.length > 1 && <span className="text-xs text-gray-400"> +{nomes.length - 1}</span>}
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap">
                                    {/* Envio pausado tem precedência visual sobre "Ativa": a página
                                        segue ativa no Meta, mas o orquestrador não dispara para ela.
                                        Sem isso a tela mente por omissão — mostrava "Ativa" para
                                        página que estava queimando requisição de app sem retorno. */}
                                    {isPausada(page) ? (
                                        <Badge
                                            variant="outline"
                                            className="bg-amber-50 text-amber-800 border-amber-200"
                                            title={page.envio_bloqueado_motivo ?? 'Envio pausado automaticamente'}
                                        >
                                            Envio pausado
                                        </Badge>
                                    ) : page.is_active ? (
                                        <Badge variant="success">Ativa</Badge>
                                    ) : (
                                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                            Inativa
                                        </Badge>
                                    )}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap overflow-hidden">
                                    {(() => {
                                        const grupo = appGroupByPageId.get(page.page_id);
                                        if (!grupo) {
                                            return <span className="text-xs text-gray-400" title="Sem grupo de app — o envio escolhe o app menos carregado">Automático</span>;
                                        }
                                        if (grupo.conflito) {
                                            return (
                                                <Badge
                                                    variant="outline"
                                                    className="bg-amber-50 text-amber-800 border-amber-200"
                                                    title="Página está em mais de um grupo de app — a trava é ignorada"
                                                >
                                                    Conflito
                                                </Badge>
                                            );
                                        }
                                        const nome = grupo.appName ?? `App #${grupo.appId}`;
                                        return (
                                            <Badge variant="outline" className="font-medium bg-gray-50 max-w-full truncate block" title={nome}>
                                                {nome}
                                            </Badge>
                                        );
                                    })()}
                                </td>
                                <td className="py-4 pr-3 whitespace-nowrap w-px">
                                    <RowMenu
                                        onSyncLeads={() => onSyncLeads?.(page)}
                                        onDetails={() => navigate(`/meta/connection/pages/${page.id}`)}
                                        onDebugToken={() => handleDebugToken(page)}
                                        onDelete={() => onDeletePage?.(page)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-1">
                    <span className="text-sm text-gray-500">
                        Página {currentPage} de {totalPages}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={currentPage <= 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                        >
                            Anterior
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                        >
                            Próxima
                        </Button>
                    </div>
                </div>
            )}

            <CampanhaDuplicadaFixModal
                grupo={grupoEmAjuste}
                onClose={() => setGrupoEmAjuste(null)}
                onSaved={() => { setGrupoEmAjuste(null); refetchDuplicadas(); }}
            />

            <AppSemTokenFixModal
                pagina={semTokenEmAjuste}
                perfis={semTokenEmAjuste ? profilesByPageId.get(semTokenEmAjuste.pageId) ?? [] : []}
                grupos={appGroups}
                onClose={() => setSemTokenEmAjuste(null)}
            />
        </div>
    );
}
